import { Box, Paper, Typography, Button, Grid, useTheme } from "@mui/material";
import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useUser } from "../contexts/useUser";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  CheckboxElement,
  SelectElement,
  TextareaAutosizeElement,
  TextFieldElement,
  useForm,
} from "react-hook-form-mui";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useErrorLogging } from "../hooks/useErrorLogging";
import { API, graphqlOperation } from "aws-amplify";
import { GraphQLResult } from "@aws-amplify/api";
import { getLLMProject } from "../graphql/queries";
import { GetLLMProjectQuery } from "../API";
import { updateLLMProjectEnterprise } from "../dataaccess/updateProjectEnterprise";

const schema = z.object({
  enterpriseAllowChat: z.boolean().optional().nullable(),
  enterpriseAcceptableUsePolicy: z.string().optional().nullable(),
  enterpriseAccessRequestEmail: z
    .string()
    .email()
    .optional()
    .nullable()
    .or(z.literal("")),
  enterpriseSupportEmail: z
    .string()
    .email()
    .optional()
    .nullable()
    .or(z.literal("")),
  enterpriseDomainWhitelist: z.string().optional().nullable(),
  enterpriseTokenBudget: z.coerce.number().optional().nullable(),
  enterpriseTokenBudgetPeriod: z.string().optional().nullable(),
  //enterpriseImageUrl: z.string().url().optional().nullable().or(z.literal("")),
});

type EnterpriseFormType = z.infer<typeof schema>;

const Enterprise: React.FC = () => {
  const { project } = useUser();
  const { logError } = useErrorLogging();
  const theme = useTheme();

  const queryClient = useQueryClient();

  const mutationUpdateProject = useMutation({
    mutationFn: updateLLMProjectEnterprise,
    onSuccess: (result, vals) => {
      queryClient.invalidateQueries({ queryKey: ["projects"] });
    },
    onError: (error) => {
      logError("Error updating project", error as any);
    },
  });

  const {
    data: projectData,
    isLoading,
    isError,
  } = useQuery({
    queryKey: ["projects", { id: project?.id }],
    queryFn: () =>
      API.graphql(
        graphqlOperation(getLLMProject, { id: project?.id })
      ) as Promise<GraphQLResult<GetLLMProjectQuery>>,
    enabled: !!project?.id,
  });

  const { handleSubmit, control, formState, getValues } =
    useForm<EnterpriseFormType>({
      values: projectData?.data?.getLLMProject ?? {},
      //values: project ?? {},
      resolver: zodResolver(schema),
      mode: "onChange",
    });

  if (isLoading) return <div>Loading...</div>;
  if (isError) return <div>Error loading project</div>;

  const onSubmit = (data: EnterpriseFormType) => {
    handleUpdate(data);
  };

  const handleUpdate = (values: EnterpriseFormType) => {
    if (!values) return;

    mutationUpdateProject.mutate({
      id: project?.id ?? "",
      enterpriseAllowChat: values.enterpriseAllowChat,
      enterpriseAcceptableUsePolicy: values.enterpriseAcceptableUsePolicy,
      enterpriseAccessRequestEmail: values.enterpriseAccessRequestEmail,
      enterpriseSupportEmail: values.enterpriseSupportEmail,
      enterpriseDomainWhitelist: values.enterpriseDomainWhitelist,
      enterpriseTokenBudget: values.enterpriseTokenBudget,
      enterpriseTokenBudgetPeriod: values.enterpriseTokenBudgetPeriod,
      //enterpriseImageUrl: values.enterpriseImageUrl,
    });
  };

  return (
    <Box p={2}>
      <Box mb={2}>
        <Paper elevation={3} style={{ borderRadius: 3, border: "1px solid #444" }}>
          <Box p={2}>
            <Typography variant="h6">Enterprise Access to LLM Chat</Typography>
            <Typography variant="caption">
              To allow centralized management of generative AI LLM chat within
              your enterprise, define the policies and rules here. This will
              allow you to manage the budget, access, confidentiality, and
              safety of the chat service in one place with confidence.
            </Typography>

            <Grid container spacing={2} padding={2}>
              <Grid item xs={6}>
                <Box display="flex" alignItems="center">
                  <CheckboxElement
                    control={control}
                    name="enterpriseAllowChat"
                    label="Allow Enterprise Chat"
                    size="small"
                  />
                  <Typography
                    variant="body2"
                    sx={{ marginRight: 2, marginLeft: 2 }}
                  >
                    (this is the master switch to enable/disable chat for your
                    enterprise.)
                  </Typography>
                </Box>
              </Grid>

              <Grid item xs={12}>
                <TextareaAutosizeElement
                  control={control}
                  name="enterpriseAcceptableUsePolicy"
                  label="Acceptable Use Policy"
                  rows={3}
                  size="small"
                  fullWidth
                />
              </Grid>

              <Grid item xs={6}>
                <TextFieldElement
                  control={control}
                  name="enterpriseAccessRequestEmail"
                  label="Access Request Email Address"
                  type="text"
                  size="small"
                  fullWidth
                />
              </Grid>
              <Grid item xs={6}>
                <TextFieldElement
                  control={control}
                  name="enterpriseSupportEmail"
                  label="Support Email Address"
                  type="text"
                  size="small"
                  fullWidth
                />
              </Grid>
              <Grid item xs={12}>
                <Typography variant="caption">
                  This whitelist of domains will be allowed to access the chat
                  service via passwordless login. For example yourcooldomain.com
                  (the bit after the @ in your email addresses). Comma separate
                  the list for multiple domains. Leave blank to only allow
                  pre-created users.
                </Typography>
                <TextFieldElement
                  sx={{ mt: 1 }}
                  control={control}
                  name="enterpriseDomainWhitelist"
                  label="Domain Whitelist"
                  type="text"
                  size="small"
                  fullWidth
                />
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption">
                  The number of tokens each user gets each period.
                </Typography>
                <TextFieldElement
                  sx={{ mt: 1 }}
                  control={control}
                  name="enterpriseTokenBudget"
                  label="Token Budget"
                  type="number"
                  size="small"
                  fullWidth
                />
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption">
                  When token replenishment happens.
                </Typography>
                <SelectElement
                  sx={{ mt: 1 }}
                  control={control}
                  name="enterpriseTokenBudgetPeriod"
                  label="Token Budget Period"
                  size="small"
                  fullWidth
                  options={[
                    { id: "", label: "None" },
                    { id: "monthly", label: "Beginning each month" },
                    { id: "weekly", label: "Beginning each week" },
                    { id: "daily", label: "Beginning each day" },
                  ]}
                />
              </Grid>
              {/*
              <Grid item xs={12}>
                <TextFieldElement
                  control={control}
                  name="enterpriseImageUrl"
                  label="Image URL"
                  type="text"
                  size="small"
                  fullWidth
                />
              </Grid> */}
            </Grid>

            <Button
              type="submit"
              variant="contained"
              onClick={handleSubmit(onSubmit)}
            >
              Save
            </Button>
          </Box>
        </Paper>
      </Box>
      <Paper elevation={3} style={{ borderRadius: 3,  border: "1px solid #444" }}>
        <Box p={2}>
          <Typography variant="h6">Enterprise LLM Chat URL / Embed</Typography>
          <Typography variant="body2" color="HighlightText">
            Your enterprise URL is:{" "}
            <a
              href={`${window.location.protocol}//${window.location.host}/enterprise/${project?.id}`}
              target="_blank"
              rel="noreferrer"
              style={{ color: theme.palette.text.primary }}
            >{`${window.location.protocol}//${window.location.host}/enterprise/${project?.id}`}</a>
            <Button
              variant="text"
              onClick={() => {
                navigator.clipboard.writeText(
                  `${window.location.protocol}//${window.location.host}/enterprise/${project?.id}`
                );
              }}
            >
              Copy
            </Button>
          </Typography>
          <Typography variant="body2">
            This is the URL that your employees can use to access the LLM chat.
            Typically your IT staff would create a cname record to point to this
            URL from your corporate domain.
          </Typography>
          <Typography variant="body2">
            <br />
            You can also embed the chat in your intranet sites using the
            following code snippet:
          </Typography>
          <pre>
            {`<iframe src="${window.location.protocol}//${window.location.host}/enterprise/${project?.id}" width="100%" height="500px" frameBorder="0"></iframe>`}
          </pre>
          <Button
            variant="text"
            onClick={() => {
              navigator.clipboard.writeText(
                `<iframe src="https://app.llmasaservice.io/enterprise/${project?.id}" width="100%" height="500px" frameBorder="0"></iframe>`
              );
            }}
          >
            Copy Embed Code
          </Button>
        </Box>
      </Paper>
    </Box>
  );
};

export default Enterprise;
